<template>

  <v-dialog v-model="isFormOpened" persistent max-width="600px">

    <v-snackbar :color="snackbar.color" v-model="snackbar.show" timeout="5000">
      <span>{{snackbar.message}}</span>
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">Close</v-btn>
      </template>
    </v-snackbar>

    <v-form ref="form" v-model="isFormValid" lazy-validation>
      <v-card>

        <!-- TOOLBAR -->
        <v-toolbar dark color="mou-highlight">
          <!-- TITLE -->
          <v-toolbar-title v-if="action == 'add'">Upload Image</v-toolbar-title>
          <v-toolbar-title v-if="action == 'edit'">Edit Image</v-toolbar-title>
          <v-spacer></v-spacer>
          <!-- CLOSE BUTTON -->
          <v-btn :disabled="isLoadingSubmit" icon dark @click="isFormOpened = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-progress-linear :style="isLoadingSubmit ? '' : 'visibility:hidden'" indeterminate color="secondary"></v-progress-linear>

        <!-- FORM INPUT -->
        <v-card-text>
          <v-container>
            <v-row>

              <!-- PHOTO -->
              <v-col cols="12" class="d-flex justify-center">
                <v-hover v-slot:default="{ hover }">
                  <div class="d-flex cursor-pointer p-relative w-100" @click="openImageUpload()">
                    <v-img height="350px" width="100%"
                      :src="isImageError ? imgPath.imgError : formData.imgSrc"
                      :lazy-src="imgPath.lazyPath"
                      v-on:error="isImageError = true"
                    >
                    </v-img>
                    <div class="p-absolute d-flex w-100 h-100 justify-center align-center" v-if="hover">
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn fab small dark color="blue-grey" class="mr-1" v-bind="attrs" v-on="on">
                              <v-icon>mdi mdi-image-search</v-icon>
                          </v-btn>
                        </template>
                        <span>Change Image</span>
                      </v-tooltip>
                    </div>
                  </div>
                </v-hover>
              </v-col>
              <v-col cols="12" class="pa-0 pt-3" style="margin-top:-70px;">
                <v-row class="ma-0">
                  <v-col cols="3" class="pa-0"></v-col>
                  <v-col cols="6" class="pa-0">
                    <v-file-input :disabled="isLoadingSubmit" class="imageInput" ref="imageInput" 
                      v-on:change="onImagePicked($event)" 
                      :rules="[
                        v => (formData.imgId ? true : (!!v)) || 'Image is required',
                        v => !v || v.size < 10000000 || 'Image size should be less than 10 MB!'
                      ]"
                      accept="image/png, image/jpeg, image/jpg"
                    ></v-file-input>
                  </v-col>
                  <v-col cols="3" class="pa-0"></v-col>
                </v-row>
              </v-col>

              <!-- POSITION -->
              {{formData.imgId}}
              <v-col cols="12" class="d-flex align-center">
                <v-text-field :disabled="isLoadingSubmit" v-model="formData.position" :rules="positionRules" min=1 type="number" label="Position*" required></v-text-field>
                <v-btn :disabled="isLoadingSubmit" icon @click="formData.position > 1 ? formData.position-- : 1"><v-icon>mdi-minus-circle</v-icon></v-btn>
                <v-btn :disabled="isLoadingSubmit" icon @click="formData.position++" ><v-icon>mdi-plus-circle</v-icon></v-btn>
              </v-col>

              <!-- DESCRIPTION -->
              <v-col cols="12">
                <v-textarea :disabled="isLoadingSubmit" v-model="formData.description" label="Description" auto-grow></v-textarea>
              </v-col>

            </v-row>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn :disabled="isLoadingSubmit" color="grey darken-1" text @click="isFormOpened = false;">Cancel</v-btn>
          <v-btn :disabled="isLoadingSubmit" color="mou-highlight" text @click="formSave()">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>

</template>

<script>
  import config from '../../config';
  export default {

    data: () => ({
      imgPath: {
        path: config.STORAGE_URL+'/gallery/',
        lazyPath: config.IMG_LAZY,
        imgEmpty: config.IMG_EMPTY,
        imgError: config.IMG_ERROR
      },

      action: null,
      isImageError: false,
      isFormOpened: false,
      isFormValid: true,
      isLoadingSubmit: false,
      snackbar: { show: false, color: '', message: ''},

      formData: {
        id: null,
        imgId: null,
        imgSrc: null,
        imgObj: null,
        position: null,
        description: null,
      },

      positionRules: [ 
        v => (!isNaN(parseInt(v))) || 'Position must be a number', 
        v => (!isNaN(parseInt(v)) && v > 0) || 'Position must be greater than 0', 
        v => (!isNaN(parseInt(v)) && (v%1 === 0)) || 'Position cannot be a decimal', 
      ]
    }),

    methods: {
      initVariable(){
        this.action = 'add';
        this.formData.id = null;
        this.formData.imgId = null;
        this.formData.imgSrc = this.imgPath.imgEmpty;
        this.formData.imgObj = null;
        this.formData.position = 1;
        this.formData.description = null;
      },

      openForm(action, item){
        if(!action){
          return;
        }

        this.initVariable();
        if(action == 'edit' && item){
          this.action = 'edit';
          this.formData.id = item.id;
          this.formData.imgId = item.imgId;
          this.formData.imgSrc = item.imgId ? this.imgPath.path+item.imgId : this.imgPath.imgEmpty;
          this.formData.imgObj = null;
          this.formData.position = item.position;
          this.formData.description = item.description;
        }
        this.isFormOpened = true;
        this.$nextTick(() => this.$refs.form.resetValidation())
      },

      formSave(){
        this.isLoadingSubmit = true;
        if(this.$refs.form.validate()){
          const target = (this.action === 'edit') ? 'backend_gallery/updateGallery' :  'backend_gallery/addGallery';
          this.$store.dispatch(target, this.formData).then((data) => {
            this.isLoadingSubmit = false;
            if(data.errors){
              this.callSnackbar('error',data.errors);
              return;
            }
            this.$emit('formGallerySaved',data)
            this.isFormOpened = false;  
          });
          return;
        }

        this.callSnackbar('error','Please fill all the required field');
        this.isLoadingSubmit = false;
      },

      openImageUpload(){
        this.$refs.imageInput.$children[0].$el.click();
      },

      onImagePicked(event){
        if(event){
          const fileReader = new FileReader();
          fileReader.readAsDataURL(event);

          fileReader.onload = (e) => {
            this.formData.imgObj = e.target.result;
          }

          this.isImageError = false;
          this.formData.imgSrc = URL.createObjectURL(event);
        }
      },

      callSnackbar(color,message){
        this.snackbar.message = message;
        this.snackbar.color = color;
        this.snackbar.show = true;
      }


    },

  }
</script>